/**
 * @fileoverview Acciones Redux para la gestión de grupos de contacto en el inbox de Mercately.
 * Proporciona funciones para cargar grupos de contacto desde el modelo ContactGroupsModelMC y actualizar el estado global.
 * @module actions/inbox/contactGroupsActions
 * @category Actions Layer
 * @since 2.0.0
 * @version 3.1.0
 */

/**
 * @typedef {Object} ContactGroupsParams
 * @description Parámetros para la consulta de grupos de contacto.
 * @property {string} [search] - Texto de búsqueda para filtrar grupos.
 * @property {number} [limit] - Límite de resultados a retornar.
 * @property {number} [offset] - Offset para paginación.
 */
import contactGroupsModelMC from '../../presentation/models/inbox/ContactGroupsModelMC';

/**
 * Acción para obtener grupos de contacto siempre, evitando duplicidad si ya está cargando.
 *
 * Implementa el patrón de capa de acciones, gestionando el estado de carga y errores.
 *
 * @function fetchContactGroupsAlways
 * @memberof module:actions/inbox/contactGroupsActions
 * @param {ContactGroupsParams} [params={}] - Parámetros de consulta para grupos de contacto.
 * @returns {Function} Función thunk para dispatch y getState.
 * @throws {Error} Si ocurre un error en la consulta al modelo.
 * @example
 * dispatch(fetchContactGroupsAlways({ search: 'VIP', limit: 10 }));
 * @see contactGroupsModelMC.getContactGroups
 */
export const fetchContactGroupsAlways = (params = {}) => async (dispatch, getState) => {
  const { loading } = getState().contactGroupsReducer || {};
  if (loading) return;
  dispatch({ type: 'START_CONTACT_GROUPS_REQUEST' });
  try {
    const { contact_groups } = await contactGroupsModelMC.getContactGroups(params);
    dispatch({ type: 'SET_CONTACT_GROUPS_DATA', data: contact_groups });
  } catch (error) {
    dispatch({ type: 'CONTACT_GROUPS_FAILURE', err: error });
  }
};

/**
 * Acción para obtener grupos de contacto solo si el estado está vacío y no está cargando.
 *
 * Implementa el patrón de capa de acciones, optimizando la carga para evitar llamadas innecesarias.
 *
 * @function fetchContactGroupsIfEmpty
 * @memberof module:actions/inbox/contactGroupsActions
 * @param {ContactGroupsParams} [params={}] - Parámetros de consulta para grupos de contacto.
 * @returns {Function} Función thunk para dispatch y getState.
 * @throws {Error} Si ocurre un error en la consulta al modelo.
 * @example
 * dispatch(fetchContactGroupsIfEmpty({ search: 'VIP' }));
 * @see contactGroupsModelMC.getContactGroups
 */
export const fetchContactGroupsIfEmpty = (params = {}) => async (dispatch, getState) => {
  const { contactGroupsList = [], loading } = getState().contactGroupsReducer || {};
  if (loading) return;
  if (!contactGroupsList.length) {
    dispatch({ type: 'START_CONTACT_GROUPS_REQUEST' });
    try {
      const { contact_groups } = await contactGroupsModelMC.getContactGroups(params);
      dispatch({ type: 'SET_CONTACT_GROUPS_DATA', data: contact_groups });
    } catch (error) {
      dispatch({ type: 'CONTACT_GROUPS_FAILURE', err: error });
    }
  } else {
    dispatch({ type: 'CONTACT_GROUPS_ALREADY_LOADED' });
  }
};